"use client";

import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { createClientLogger, serializeError } from "@/lib/client-logger";

const log = createClientLogger("error-boundary");

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState<string | null>(null);
  const maxRetries = 3;

  useEffect(() => {
    log.error("Unhandled error caught by error boundary", {
      error: serializeError(error),
      ...(error.digest ? { digest: error.digest } : {})
    });
  }, [error]);

  const getErrorString = (err: unknown): string => {
    if (err instanceof Error) {
      return (err as Error).message;
    }
    if (typeof err === "string") {
      return err;
    }
    return "Retry failed";
  };

  const handleRetry = useCallback(async () => {
    if (isRetrying || retryCount >= maxRetries) return;

    setIsRetrying(true);
    setLastError(null);

    try {
      reset();
    } catch (err: unknown) {
      setLastError(getErrorString(err));
      setRetryCount((prev) => prev + 1);
    } finally {
      setIsRetrying(false);
    }
  }, [reset, isRetrying, retryCount]);

  const getErrorMessage = () => {
    if (lastError) return lastError;
    if (error?.message) return error.message;
    return "We encountered an unexpected error. Don't worry, our team has been notified and we're working to fix it.";
  };

  const canRetry = retryCount < maxRetries;

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center bg-gray-50 p-4">
      {/* Background blur effect */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center blur-sm">
        <div className="mx-auto max-w-4xl space-y-6 p-6">
          {/* Skeleton header */}
          <div className="mb-6">
            <div className="h-10 w-64 animate-pulse rounded-md bg-gray-200"></div>
          </div>

          {/* Skeleton content cards */}
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl border border-gray-200 bg-white p-6"
              >
                <div className="space-y-3">
                  <div className="h-4 w-48 animate-pulse rounded bg-gray-200"></div>
                  <div className="h-4 w-full animate-pulse rounded bg-gray-200"></div>
                  <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Error overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm">
        <div className="mx-4 max-w-md space-y-6 rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-lg">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
            <AlertTriangle className="h-6 w-6 text-red-500" />
          </div>

          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-900">
              Something went wrong
            </h3>
            <p className="text-sm leading-relaxed text-gray-600">
              {getErrorMessage()}
            </p>
            {error?.digest && (
              <p className="text-xs text-gray-500">Error ID: {error.digest}</p>
            )}
          </div>

          <div className="flex w-full flex-col gap-3">
            <Button
              onClick={handleRetry}
              disabled={isRetrying || !canRetry}
              className={`flex w-full items-center justify-center gap-2 ${
                canRetry
                  ? ""
                  : "cursor-not-allowed bg-gray-200 text-gray-400 hover:bg-gray-200"
              }`}
              aria-label={isRetrying ? "Retrying..." : "Retry"}
            >
              <RefreshCw
                className={`h-4 w-4 ${isRetrying ? "animate-spin" : ""}`}
              />
              {isRetrying ? "Retrying..." : "Try Again"}
            </Button>

            {retryCount > 0 && canRetry && (
              <p className="text-xs text-gray-500">
                Attempt {retryCount} of {maxRetries}
              </p>
            )}

            {!canRetry && (
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="w-full"
              >
                Reload Page
              </Button>
            )}

            <Button
              onClick={() => (window.location.href = "/")}
              variant="ghost"
              className="flex w-full items-center justify-center gap-2"
            >
              <Home className="h-4 w-4" />
              Go Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
